import { Database, FileText, Search } from "lucide-react";
import { useEffect, useState } from "react";

export default function CompanyList({ 
  companies = [], 
  selectedId, 
  onSelect,
  onCompanySelect // New prop to trigger data fetch when company is selected
}) {
  const [searchTerm, setSearchTerm] = useState("");
  
  const safeCompanies = companies.map((c) => ({
    id: c.companyID ?? c.companyId ?? c.id,
    name: c.companyName ?? "Unknown",
    email: c.officialEmail ?? "",
    hasSql: c.sqlAnalyticsStatus?.toLowerCase() === "success",
    hasFile: c.fileAnalyticsStatus?.toLowerCase() === "success",
    hasMedia: c.mediaAnalyticsStatus?.toLowerCase() === "success",
    isActive: c.companyStatus === 1,
  }));

  // Filter companies based on search term
  const filteredCompanies = safeCompanies.filter(comp =>
    comp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    comp.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle company selection - this will trigger data fetch in parent
  const handleCompanySelect = (companyId) => {
    onSelect(companyId);
    if (onCompanySelect) {
      onCompanySelect(companyId);
    }
  };

  return (
    <div className="w-full md:w-80 flex-shrink-0 bg-slate-50 dark:bg-slate-900/40 border-r border-slate-100 dark:border-slate-700 p-6 sm:p-8 flex flex-col">
      <div className="mb-6">
        <h3 className="text-xl font-bold text-slate-800 dark:text-white">
          Data Sources
        </h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          Manage tenant integrations
        </p>
      </div>

      <div className="relative mb-6">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          type="text"
          placeholder="Search tenants…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1d57a5] transition-all"
        />
      </div>

      <div className="space-y-2 overflow-y-auto hide-scrollbar flex-1">
        {filteredCompanies.length === 0 ? (
          <p className="text-sm text-slate-400 text-center mt-8">
            {searchTerm ? "No tenants match your search." : "No tenants found."}
          </p>
        ) : (
          filteredCompanies.map((comp) => (
            <button
              key={comp.id}
              onClick={() => handleCompanySelect(comp.id)}
              className={`w-full flex items-center gap-3 p-3.5 rounded-2xl transition-all text-left border ${
                selectedId === comp.id
                  ? "bg-white dark:bg-slate-800 border-[#1d57a5] shadow-sm shadow-[#1d57a5]/10 dark:border-[#4a8ce3]"
                  : "border-transparent hover:bg-white dark:hover:bg-slate-800 hover:border-slate-200 dark:hover:border-slate-700"
              }`}
            >
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm transition-colors ${
                  selectedId === comp.id
                    ? "bg-[#1d57a5] text-white dark:bg-[#4a8ce3] dark:text-slate-900 shadow-md"
                    : "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                }`}
              >
                {comp.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 overflow-hidden">
                <h4
                  className={`text-sm font-semibold truncate transition-colors ${
                    selectedId === comp.id
                      ? "text-[#1d57a5] dark:text-[#4a8ce3]"
                      : "text-slate-700 dark:text-slate-300"
                  }`}
                >
                  {comp.name}
                </h4>
                <div className="flex items-center gap-1.5 mt-1">
                  {comp.hasSql && (
                    <Database size={10} className="text-amber-500" />
                  )}
                  {comp.hasFile && (
                    <FileText size={10} className="text-[#36a852]" />
                  )}
                  {comp.hasMedia && (
                    <MediaDevices size={10} className="text-[#36a852]" />
                  )}
                  <span className="text-[10px] text-slate-500 truncate">
                    {comp.hasSql && comp.hasFile
                      ? "SQL + Files"
                      : comp.hasSql
                        ? "SQL Only"
                        : comp.hasFile
                          ? "Files Only"
                          : "No integrations"}
                  </span>
                </div>
              </div>
              <div
                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  comp.isActive ? "bg-green-400" : "bg-slate-300"
                }`}
              />
            </button>
          ))
        )}
      </div>
    </div>
  );
}